var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import angular from "angular";
import { reactive, createApp, ref, onMounted, h, resolveDirective, withDirectives } from "vue";
function evaluateEvents(events, scope) {
  if (!(events && angular.isObject(events))) {
    return {};
  }
  const evaluatedEvents = Object.keys(events).reduce((accumulator, key) => {
    const fn = scope.$eval(events[key]);
    if (!angular.isFunction(fn)) {
      accumulator[key] = fn;
      console.warn(`[ngVue] Bound a non-function as an event handler (${key})`);
    } else {
      accumulator[key] = function() {
        return scope.$evalAsync(() => fn.apply(null, arguments));
      };
    }
    return accumulator;
  }, {});
  return evaluatedEvents;
}
function evaluateValues(expressions, scope) {
  if (!expressions)
    return {};
  const evaluatedValues = Object.keys(expressions).reduce((accumulator, prop) => {
    if (prop === "__ngvue_props__") {
      Object.assign(accumulator, scope.$eval(expressions.__ngvue_props__));
    } else {
      accumulator[prop] = scope.$eval(expressions[prop]);
    }
    return accumulator;
  }, {});
  return evaluatedValues;
}
function getTypeOf(value) {
  return value.constructor.name;
}
const transformers = {
  Object: (value) => [value],
  Array: (value) => value,
  String: (value) => value.split(/\s*,\s*/g).filter(Boolean).map((name) => ({ name }))
};
function evaluateDirectives(directiveExpression, scope) {
  if (!directiveExpression)
    return [];
  const directives = scope.$eval(directiveExpression);
  const transformer = transformers[getTypeOf(directives)];
  return transformer ? transformer(directives) : [];
}
const SPECIAL_ATTRS = ["class", "style"];
function extractSpecialAttributes(attributes, removeAttrFn) {
  return SPECIAL_ATTRS.reduce((accumulator, key) => {
    accumulator[key] = attributes[key] || "";
    removeAttrFn(key);
    return accumulator;
  }, {});
}
const excludedAttributes = [
  "vProps",
  "vOn",
  "vDirectives",
  "watchDepth",
  "ng",
  ...SPECIAL_ATTRS
];
const excludePattern = new RegExp(`^(${excludedAttributes.join("|")})`, "i");
function extractHtmlAttributes(attributes) {
  return Object.keys(attributes).filter((attribute) => {
    const isExcludedAttribute = excludePattern.test(attribute);
    const isAngularInternal = attribute[0] === "$";
    return !(isExcludedAttribute || isAngularInternal);
  });
}
function lowerFirst([first, ...rest]) {
  return first.toLowerCase() + rest.join("");
}
const replacementMap = {
  vProps: "",
  vOn: "on"
};
Object.freeze(replacementMap);
function extractExpressionName(expression, replaceKey) {
  const pattern = new RegExp(`^${replaceKey}`, "i");
  if (!pattern.test(expression))
    return null;
  const name = expression.replace(pattern, replacementMap[replaceKey]);
  return lowerFirst(name);
}
const attributeMap = {
  props: "vProps",
  on: "vOn"
};
Object.freeze(attributeMap);
function isAttributeMapKey(expressionType) {
  return expressionType in attributeMap;
}
function extractExpressions(expressionType, attributes, removeAttrFn) {
  let expressions = [];
  let key;
  let extractExpressionFn;
  let baseExpressionMap = {};
  if (isAttributeMapKey(expressionType)) {
    key = attributeMap[expressionType];
    const objectExpression = attributes[key];
    if (objectExpression !== void 0) {
      baseExpressionMap.__ngvue_props__ = objectExpression;
      removeAttrFn(key);
    }
    const propRegExp = new RegExp(`^${key}.+`, "i");
    expressions = Object.keys(attributes).filter((attr) => propRegExp.test(attr));
    extractExpressionFn = (accumulator, attrKey) => {
      const exprName = extractExpressionName(attrKey, key);
      if (exprName) {
        accumulator[exprName] = attributes[attrKey];
        removeAttrFn(attrKey);
      }
      return accumulator;
    };
  } else {
    expressions = extractHtmlAttributes(attributes);
    extractExpressionFn = (accumulator, attrKey) => {
      const denormalizedAttr = attributes.$attr[attrKey];
      accumulator[denormalizedAttr] = attributes[attrKey] === "" ? `${attrKey}` : attributes[attrKey];
      removeAttrFn(attrKey);
      return accumulator;
    };
  }
  const expressionsMap = expressions.reduce(extractExpressionFn, baseExpressionMap);
  return expressionsMap;
}
function getExpressions(attributes, removeAttrFn) {
  return {
    props: extractExpressions("props", attributes, removeAttrFn) || {},
    events: extractExpressions("on", attributes, removeAttrFn) || {},
    attrs: extractExpressions("attrs", attributes, removeAttrFn) || {}
  };
}
function watchExpressions(expressions, state, scope, options = { depth: "reference" }) {
  if (!expressions)
    return;
  const watcher = getWatcher(expressions, state);
  switch (options.depth) {
    case "value":
      watcher((expression, setter) => scope.$watch(expression, setter, true));
      break;
    case "collection":
      watcher((expression, setter) => scope.$watchCollection(expression, setter));
      break;
    case "reference":
    default:
      watcher((expression, setter) => scope.$watch(expression, setter));
      break;
  }
}
function isNgPropsObject(name, value) {
  return name === "__ngvue_props__" && angular.isObject(value);
}
function getWatcher(expressions, state) {
  return (callback) => {
    Object.keys(expressions).forEach((name) => {
      const setter = (value) => {
        if (isNgPropsObject(name, value)) {
          Object.keys(value).forEach((key) => {
            state[key] = value[key];
          });
        } else {
          state[name] = value;
        }
      };
      callback(expressions[name], setter);
    });
  };
}
const store = {};
function getInstanceState(id) {
  if (!store[id]) {
    store[id] = {
      props: reactive({}),
      attrs: reactive({}),
      special: reactive({})
    };
  }
  return store[id];
}
function ngVueLinker(Component, jqElement, attrs, scope, $injector) {
  const instanceKey = Symbol("ngVueInstanceKey");
  const state = getInstanceState(instanceKey);
  const removeAttrFn = getAttrRemoveFunction(jqElement, attrs);
  const attrExpressions = getExpressions(attrs, removeAttrFn);
  const events = evaluateEvents(attrExpressions.events, scope);
  const ngVueDirectives = evaluateDirectives(attrs.vDirectives, scope);
  Object.assign(state.props, evaluateValues(attrExpressions.props, scope));
  Object.assign(state.attrs, evaluateValues(attrExpressions.attrs, scope));
  Object.assign(state.special, extractSpecialAttributes(attrs, removeAttrFn));
  const options = { depth: attrs.watchDepth };
  removeAttrFn("watchDepth");
  watchExpressions(attrExpressions.props, state.props, scope, options);
  watchExpressions(attrExpressions.attrs, state.attrs, scope, options);
  let html = getInnerHtml(jqElement[0]);
  let vueInstance = createAppInstance(Component, html, state, events, ngVueDirectives);
  loadNgVueGlobals(vueInstance, $injector);
  vueInstance.mount(jqElement[0]);
  scope.$on("$destroy", () => {
    vueInstance.unmount();
    if (vueInstance._container) {
      angular.element(vueInstance._container).remove();
    }
    vueInstance = null;
  });
}
function createAppInstance(Component, html, state, events, ngVueDirectives) {
  return createApp({
    name: `NgVue-${Component.name || "UnnamedComponent"}`,
    setup() {
      const slot = ref(null);
      onMounted(() => {
        var _a, _b;
        if (html) {
          try {
            (_b = (_a = slot.value) == null ? void 0 : _a.parentElement) == null ? void 0 : _b.replaceChild(html, slot.value);
          } catch (err) {
            console.error("Attempted to insert content into slot and something went wrong. Inserting content, especially angular content, into a components slot is not advisable. This is a pretty hacky implementation in ngVue. Open an issue at https://github.com/jaredmcateer/ngvue3/issues with details.");
          }
        }
      });
      return () => {
        let node = h(Component, __spreadValues(__spreadValues(__spreadValues(__spreadValues({}, state.props), state.attrs), state.special), events), () => [h("span", { ref: slot })]);
        if (ngVueDirectives.length > 0) {
          const directives = ngVueDirectives.filter((d) => !!resolveDirective(d.name)).map((d) => {
            const directive = resolveDirective(d.name);
            return [directive, d.value, d.arg, d.modifiers];
          });
          node = withDirectives(node, directives);
        }
        return node;
      };
    }
  });
}
function loadNgVueGlobals(vueInstance, $injector) {
  const $ngVue = $injector.has("$ngVue") ? $injector.get("$ngVue") : null;
  if ($ngVue) {
    $ngVue.initNgVuePlugins(vueInstance);
    $ngVue.getVuePlugins().forEach((plugin) => vueInstance.use(plugin));
    $ngVue.getInjectables().forEach(([key, value]) => vueInstance.provide(key, value));
    Object.entries($ngVue.getVueDirectives()).forEach(([name, directive]) => vueInstance.directive(name, directive));
  }
}
function getInnerHtml(element) {
  if (element.innerHTML.trim()) {
    let html;
    if (element.children.length === 0) {
      const span = document.createElement("span");
      span.innerHTML = element.innerHTML.trim();
      html = span;
    } else {
      html = element.children[0];
    }
    return html;
  }
}
function getAttrRemoveFunction(element, attributes) {
  return (attr) => {
    const denormalizedAttr = attributes.$attr[attr];
    element.removeAttr(denormalizedAttr);
  };
}
function ngVueComponentFactory($injector) {
  return (component, ngDirectiveConfig) => {
    const config = {
      restrict: "E",
      link(scope, elem, attrs) {
        ngVueLinker(component, elem, attrs, scope, $injector);
      }
    };
    return angular.extend(config, ngDirectiveConfig);
  };
}
ngVueComponentFactory.$inject = ["$injector"];
function ngVueComponent(name, Component, ngDirectiveConfig) {
  return [
    name,
    [
      "createVueComponent",
      (createVueComponent) => createVueComponent(Component, ngDirectiveConfig)
    ]
  ];
}
class NgVueProvider {
  constructor($injector) {
    __publicField(this, "$injector");
    __publicField(this, "$get");
    __publicField(this, "pluginHooks", []);
    __publicField(this, "pluginConfig", {});
    __publicField(this, "injectables", []);
    __publicField(this, "nativeVuePlugins", []);
    __publicField(this, "nativeVueDirectives", {});
    this.$injector = $injector;
    this.$get = [
      "$injector",
      ($injector2) => {
        const initNgVuePlugins = (app) => {
          const callback = (hook) => {
            hook($injector2, app);
          };
          this.callHooks(this.pluginHooks, callback);
        };
        return {
          initNgVuePlugins,
          getInjectables: () => this.injectables,
          getVuePlugins: () => this.nativeVuePlugins,
          getVueDirectives: () => this.nativeVueDirectives
        };
      }
    ];
  }
  get plugins() {
    return this.pluginConfig;
  }
  provide(name, value) {
    this.injectables.push([name, value]);
  }
  use(vuePlugin) {
    this.nativeVuePlugins.push(vuePlugin);
  }
  directive(name, vueDirective) {
    this.nativeVueDirectives[name] = vueDirective;
  }
  installNgVuePlugin(plugin) {
    const { $name, $config, $plugin } = plugin();
    this.pluginHooks.push($plugin);
    this.pluginConfig[$name] = $config;
  }
  callHooks(hooks, callback) {
    if (hooks) {
      hooks.forEach((hook) => callback(hook));
    }
  }
}
__publicField(NgVueProvider, "$inject", ["$injector"]);
let ngVuePluginsModule;
function useNgVuePlugins() {
  if (!ngVuePluginsModule) {
    ngVuePluginsModule = angular.module("ngVue.plugins", []).provider("$ngVue", ["$injector", NgVueProvider]);
  }
  return ngVuePluginsModule.name;
}
let ngVueModule;
function useNgVue() {
  if (!ngVueModule) {
    ngVueModule = angular.module("ngVue", []).factory("createVueComponent", ngVueComponentFactory);
  }
  return ngVueModule.name;
}
export { ngVueComponent, useNgVue, useNgVuePlugins };
//# sourceMappingURL=main.mjs.map
